import React, { useEffect, useState, useRef } from "react"
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'
import { useReactToPrint } from 'react-to-print'
import { toast } from 'react-toastify'
import DatePicker from "react-datepicker"
import html2canvas from 'html2canvas'

import Poster from './Poster'
import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { httpOk, GET, PUT, POST, DELETE, FILE } from 'helpers/api'
import { formatDate, returnSign } from 'helpers/helpers'

/**
 * MIJOZ SHIKOYAT / TAKLIFLARI.
 *
 * Mijoz ofisdagi A5 plakatdagi QR ni skanerlaydi -> Telegram bot ochiladi ->
 * shikoyatini yozadi (rasm/video ham) -> shikoyat DARHOL mas'ullar guruhiga
 * tushadi va shu ro'yxatda paydo bo'ladi.
 *
 * Bu yerdan yozilgan javob mijozning O'ZIGA Telegram orqali boradi.
 */
function Index() {
	const { t } = useTranslation()

	const posterRef = useRef()
	const handlePrint = useReactToPrint({ content: () => posterRef.current })

	const [posterSaving, setPosterSaving] = useState(false)

	/**
	 * PLAKATNI RASM QILIB SAQLASH — «boshqa printerdan chiqaramiz» (user talabi).
	 *
	 * ⚠️ `useCORS` va `allowTaint: false`: orqa fon BOSHQA hostdan keladi
	 *    (backend.tameer.uz), shusiz canvas «iflos» bo'lib qoladi va
	 *    toDataURL xato beradi — rasm bo'sh chiqardi.
	 * ⚠️ `scale: 3` — A5 varaq ~1240x1750 px bo'ladi, bu bosma uchun yetarli
	 *    (ekran o'lchamida saqlansa bosilganda donador chiqadi).
	 */
	async function downloadPoster() {
		if (!posterRef.current) return
		setPosterSaving(true)
		try {
			const canvas = await html2canvas(posterRef.current, {
				scale: 3,
				useCORS: true,
				backgroundColor: null,
				logging: false,
			})
			const link = document.createElement('a')
			link.download = 'shikoyat-plakat-A5.png'
			link.href = canvas.toDataURL('image/png')
			link.click()
		} catch (e) {
			toast.error(t('error'))
		} finally {
			setPosterSaving(false)
		}
	}

	const [data, setData] = useState({})
	const [poster, setPoster] = useState({})
	const [modalPoster, setModalPoster] = useState(false)
	/* Plakat orqa foni — korxonaning O'Z mahsulot rasmi.
	   Dastur naqsh chizib bermaydi: brend uslubi har korxonada boshqacha. */
	async function uploadPosterBg(file, remove = false) {
		const form = new FormData()
		if (remove) {
			form.append('remove', '1')
		} else {
			if (!file) return
			/* ⚠️ RASM BRAUZERDA KICHIKLASHTIRILADI.
			   Sabab: PHP da `upload_max_filesize = 2M` (lokalda ham, serverда ham),
			   telefondan olingan foto esa 3-6 MB bo'ladi — PHP uni JIMGINA tashlab
			   yuboradi va backend «rasm yuborilmagan» deb qaytaradi. Odam esa
			   «yuklamayapti» deb qoladi. A5 plakat uchun 1800px dan katta rasm
			   baribir kerak emas. */
			const prepared = await shrinkImage(file)
			if (!prepared) {
				toast.error(t('poster_bg_invalid'))
				return
			}
			form.append('image', prepared, 'poster.jpg')
		}

		const response = await FILE('/complaints-poster-bg', form, { loader: true })
		if (httpOk(response)) {
			setPoster((p) => ({ ...p, poster_bg: response.data?.poster_bg ?? null }))
		}
	}

	/** Rasmni eng katta tomoni bo'yicha 1800px ga tushiradi va JPEG qiladi */
	function shrinkImage(file, maxSide = 1800, quality = 0.85) {
		return new Promise((resolve) => {
			if (!file.type || !file.type.startsWith('image/')) return resolve(null)

			const reader = new FileReader()
			reader.onerror = () => resolve(null)
			reader.onload = () => {
				const img = new Image()
				img.onerror = () => resolve(null)
				img.onload = () => {
					const scale = Math.min(1, maxSide / Math.max(img.width, img.height))
					const w = Math.round(img.width * scale)
					const h = Math.round(img.height * scale)

					const canvas = document.createElement('canvas')
					canvas.width = w
					canvas.height = h
					const ctx = canvas.getContext('2d')
					ctx.drawImage(img, 0, 0, w, h)

					canvas.toBlob((blob) => resolve(blob || null), 'image/jpeg', quality)
				}
				img.src = reader.result
			}
			reader.readAsDataURL(file)
		})
	}
	const [modalReply, setModalReply] = useState(false)
	const [selectedItem, setSelectedItem] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		search: '',
		status: '',
		start_date: null,
		end_date: null,
	})
	const [pagination, setPagination] = useState({ url: '/complaints', response: null })

	async function searchWithFilter(override = null) {
		const f = override && typeof override === 'object' ? override : filterData
		var filterUrl = '/complaints'
		var urlParams = ''

		if (f.status) urlParams += returnSign(urlParams) + 'status=' + f.status
		if (f.start_date) urlParams += returnSign(urlParams) + 'start_date=' + f.start_date
		if (f.end_date) urlParams += returnSign(urlParams) + 'end_date=' + f.end_date
		if (f.search) urlParams += returnSign(urlParams) + 'search=' + f.search
		if (f.page) urlParams += returnSign(urlParams) + 'page=' + f.page
		if (f.size) urlParams += returnSign(urlParams) + 'size=' + f.size

		setFilterDataExist(!!(f.status || f.start_date || f.end_date || f.search))
		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			if (dropdownIsOpen) setDropdownIsOpen(false)
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}
	}

	async function openPoster() {
		const response = await GET('/complaints-poster', {}, { loader: true })
		if (httpOk(response)) {
			setPoster(response.data)
			setModalPoster(true)
		}
	}

	/* Holat: 1 = yangi, 2 = ko'rildi, 3 = hal qilindi */
	async function setStatus(item, status) {
		const response = await PUT('/complaints-status', { id: item.id, status: status }, { loader: true })
		if (httpOk(response)) searchWithFilter()
	}

	function toggleModalReply(bool = false, item = null) {
		if (bool) setSelectedItem({ ...item, answer: item?.answer ?? '' })
		setModalReply(bool)
	}

	async function sendReply(e) {
		e.preventDefault()

		const response = await POST('/complaints-reply', {
			id: selectedItem.id,
			answer: selectedItem.answer,
		}, { loader: true })

		if (httpOk(response)) {
			toggleModalReply(false)
			searchWithFilter()
		}
	}

	async function deleteItem(item) {
		if (!window.confirm(`#${item.id} — ${t('delete')}?`)) return
		const response = await DELETE(`/complaints/${item.id}`, {}, { loader: true })
		if (httpOk(response)) searchWithFilter()
	}

	function resetFilter() {
		const cleared = { search: '', status: '', start_date: null, end_date: null }
		setFilterData(cleared)
		searchWithFilter(cleared)
	}

	async function paginate(page, size) {
		setFilterData({ ...filterData, 'page': page, 'size': size })
	}

	/* MAVZU — operator qo'yadi (botda so'ralsa mijozga ortiqcha qadam bo'lardi).
	   Oyda nimadan ko'p shikoyat kelgani shu ustundan chiqadi. */
	const TOPICS = ['quality', 'delivery', 'price', 'staff', 'suggestion', 'other']

	/* Rasm manzili — chekdagi logotip bilan bir xil naqsh (backend public/uploads).
	   Lokal ishlaganda REACT_APP_API_URL orqali backend manzili olinadi. */
	function mediaUrl(name) {
		const api = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8000/api`
		return api.replace(/\/api\/?$/, '') + '/uploads/complaints/' + name
	}

	async function setTopic(item, topic) {
		const response = await PUT('/complaints-topic', { id: item.id, topic }, { loader: false })
		if (httpOk(response)) {
			setData((d) => ({
				...d,
				data: (d.data ?? []).map((x) => (x.id === item.id ? { ...x, topic } : x)),
			}))
		}
	}

	function statusName(status) {
		switch (Number(status)) {
			case 1: return t('complaint_new')
			case 2: return t('complaint_seen')
			case 3: return t('complaint_resolved')
			default: return '-'
		}
	}

	function statusClass(status) {
		switch (Number(status)) {
			case 1: return 'bg-danger'
			case 2: return 'bg-warning'
			case 3: return 'bg-success'
			default: return 'bg-secondary'
		}
	}

	useEffect(() => {
		searchWithFilter(false)
	}, [filterData.search, filterData.status, filterData.page, filterData.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center h-100">
					{t('complaints')}
				</h4>
				<div className="d-flex gap-2">
					<button className="btn btn-outline-primary btn-wide" onClick={openPoster}>
						<i className="uil uil-qrcode-scan me-1"></i>
						{t('poster_button')}
					</button>
				</div>
			</div>

			<div className="row mb-3">
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('complaint_new')}</div>
							<h5 className="text-danger">{data?.counts?.yangi ?? 0}</h5>
						</div>
					</div>
				</div>
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('complaint_seen')}</div>
							<h5 className="text-warning">{data?.counts?.korildi ?? 0}</h5>
						</div>
					</div>
				</div>
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('complaint_resolved')}</div>
							<h5 className="text-success">{data?.counts?.hal_qilindi ?? 0}</h5>
						</div>
					</div>
				</div>
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('average_rating')}</div>
							<h5>
								{data?.counts?.ortacha_baho
									? <>{data.counts.ortacha_baho} <span className="text-warning">⭐</span></>
									: '—'}
							</h5>
							{/* O'rtacha javob vaqti — boshqaruv ko'rsatkichi: shikoyatga qancha
							    vaqtda javob berilyapti. Javob berilganlar bo'yicha hisoblanadi. */}
							{!!data?.counts?.ortacha_javob_soat &&
								<div className="fz-14 text-muted">
									{t('avg_answer_time')}: {data.counts.ortacha_javob_soat} {t('hour_short')}
								</div>
							}
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<FilterSearch filterData={filterData} setFilterData={setFilterData}></FilterSearch>
							<div>
								<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>
									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('period')}</label>
												<div className="calendar-input">
													<DatePicker className="form-control" dateFormat="dd.MM.yyyy"
														selected={filterData.start_date ? new Date(filterData.start_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'start_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
											<div className="form-group col-md-6">
												<label className="invisible">.</label>
												<div className="calendar-input">
													<DatePicker className="form-control" dateFormat="dd.MM.yyyy"
														selected={filterData.end_date ? new Date(filterData.end_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'end_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter()}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>

							{/* Holat bo'yicha tez o'tish */}
							<div className="btn-group">
								<button className={`btn ${filterData.status === '' ? 'btn-primary' : 'btn-outline-primary'}`}
									onClick={() => setFilterData({ ...filterData, 'status': '' })}>
									{t('all')}
								</button>
								<button className={`btn ${filterData.status === '1' ? 'btn-danger' : 'btn-outline-danger'}`}
									onClick={() => setFilterData({ ...filterData, 'status': '1' })}>
									{t('complaint_new')}
								</button>
								<button className={`btn ${filterData.status === '3' ? 'btn-success' : 'btn-outline-success'}`}
									onClick={() => setFilterData({ ...filterData, 'status': '3' })}>
									{t('complaint_resolved')}
								</button>
								{/* KECHIKKANLAR — 24 soatdan beri javobsiz. Soni 0 bo'lsa tugma chiqmaydi. */}
								{!!data?.counts?.kechikkan &&
									<button className={`btn ${filterData.overdue ? 'btn-warning' : 'btn-outline-warning'}`}
										onClick={() => setFilterData({ ...filterData, 'overdue': filterData.overdue ? '' : '1', 'status': '' })}>
										<i className="uil uil-clock me-1"></i>
										{t('complaint_overdue')} ({data.counts.kechikkan})
									</button>
								}
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{(filterData.start_date && filterData.end_date) && <span>|</span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
								{filterData.search &&
									<span className="me-2"><b>{t('search')}: </b><span>{filterData.search}</span></span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '13%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '17%' }}>{t('client')}</th>
									<th style={{ 'width': '38%' }}>{t('text')}</th>
									<th style={{ 'width': '10%' }}>{t('topic')}</th>
									<th style={{ 'width': '7%' }} className="text-center">{t('rating')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('status')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									/* 24 soatdan beri javobsiz qatorni ajratamiz — «unutilib qolgan»
									   murojaat ko'zdan qochmasin */
									<tr key={index} className={item.is_overdue ? 'table-warning' : ''}>
										<td className="text-center">{item.id}</td>
										<td className="text-center text-nowrap">
											{formatDate(item.created_at, 'dd.MM.yyyy HH:mm')}
											{item.is_overdue &&
												<div className="fz-14 text-danger">
													<i className="uil uil-clock me-1"></i>
													{Math.round(item.hours_open)} {t('hour_short')}
												</div>
											}
										</td>
										<td>
											<div>{item.client_name || item.first_name || '—'}</div>
											{item.phone && <div className="fz-14 text-muted">{item.phone}</div>}
											{item.username && <div className="fz-14 text-muted">@{item.username}</div>}
										</td>
										<td>
											<div className="d-flex gap-2">
												{/* RASM/VIDEO — serverga yuklab olingan, Telegramga o'tish shart emas */}
												{item.media_path &&
													<div className="flex-shrink-0">
														{item.media_type === 'video'
															? <a href={mediaUrl(item.media_path)} target="_blank" rel="noopener noreferrer"
																className="btn btn-sm btn-outline-primary">
																<i className="uil uil-video"></i>
															</a>
															: <a href={mediaUrl(item.media_path)} target="_blank" rel="noopener noreferrer">
																<img src={mediaUrl(item.media_path)} alt=""
																	style={{
																		width: '64px', height: '64px', objectFit: 'cover',
																		borderRadius: '6px', border: '1px solid #dee2e6',
																	}} />
															</a>
														}
													</div>
												}
												<div className="flex-grow-1">
													<div style={{ 'whiteSpace': 'pre-wrap' }}>{item.text}</div>
													{/* Fayl yuklab olinmagan (eski yozuv yoki yuklashda xato) */}
													{(item.media_type && !item.media_path) &&
														<div className="fz-14 text-muted">
															<i className="uil uil-image me-1"></i>
															{t('media_in_telegram')}
														</div>
													}
													{item.answer &&
														<div className="fz-14 mt-1 text-success">
															<b>{t('answer')}:</b> {item.answer}
														</div>
													}
												</div>
											</div>
										</td>
										<td>
											{/* MAVZU — bir bosishda saqlanadi */}
											<select className="form-select form-select-sm"
												value={item.topic ?? ''}
												onChange={(e) => setTopic(item, e.target.value)}>
												{/* «—» ni ham tanlash mumkin: xato belgilangan mavzuni bekor qilish uchun */}
												<option value="">—</option>
												{TOPICS.map((tp) => (
													<option key={tp} value={tp}>{t('topic_' + tp)}</option>
												))}
											</select>
										</td>
										<td className="text-center text-nowrap">
											{item.rating ? '⭐'.repeat(item.rating) : '—'}
										</td>
										<td className="text-center">
											<span className={`badge ${statusClass(item.status)}`}>
												{statusName(item.status)}
											</span>
										</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>
													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => toggleModalReply(true, item)}>
															<i className="uil-comment-alt-message"></i>
															{t('answer')}
														</div>
														{item.status === 1 &&
															<div className="table-dropdown-item"
																onClick={() => setStatus(item, 2)}>
																<i className="uil-eye"></i>
																{t('complaint_seen')}
															</div>
														}
														{item.status !== 3 &&
															<div className="table-dropdown-item"
																onClick={() => setStatus(item, 3)}>
																<i className="uil-check"></i>
																{t('complaint_resolved')}
															</div>
														}
														<div className="table-dropdown-item text-danger"
															onClick={() => deleteItem(item)}>
															<i className="uil-trash-alt"></i>
															{t('delete')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination pagination={pagination} paginate={paginate}></FilterPagination>
					}
				</div>
			</div>

			{/* JAVOB — mijozning O'ZIGA Telegram orqali boradi */}
			<Modal show={modalReply} animation={false} centered
				dialogClassName="update-modal-width" backdrop="static"
				onHide={() => toggleModalReply(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('answer')} #{selectedItem.id}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={sendReply} autoComplete="off">
						<div className="bg-light p-2 mb-3 fz-14" style={{ 'whiteSpace': 'pre-wrap' }}>
							{selectedItem.text}
						</div>

						{/* TAYYOR MATNLAR — bosilsa maydonga qo'yiladi, keyin tahrirlash mumkin.
						    Har safar noldan yozilmasin. */}
						<div className="d-flex flex-wrap gap-1 mb-2">
							{['t1', 't2', 't3', 't4'].map((k) => (
								<button key={k} type="button" className="btn btn-sm btn-outline-secondary"
									onClick={() => setSelectedItem({ ...selectedItem, 'answer': t('reply_' + k) })}>
									{t('reply_' + k + '_label')}
								</button>
							))}
						</div>

						<div className="form-group">
							<label>{t('answer')}<span className="required-mark">*</span></label>
							<textarea className="form-control" rows="4" maxLength="2000" autoFocus
								value={selectedItem.answer ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'answer': e.target.value })} />
						</div>
						<div className="text-muted fz-14">{t('answer_hint')}</div>
						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModalReply(false)} tabIndex="-1">
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100"
								disabled={!selectedItem.answer}>
								{t('send')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>

			{/* A5 QR PLAKAT */}
			<Modal show={modalPoster} animation={false} centered size="lg"
				onHide={() => setModalPoster(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('poster_button')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{!poster.bot_url &&
						<div className="alert alert-warning">
							{t('poster_no_bot')}
						</div>
					}

					{/* ORQA FON — korxonaning O'Z mahsulot rasmi (dastur chizmaydi) */}
					<div className="d-flex flex-wrap gap-2 mb-3 align-items-center justify-content-center">
						<label className="btn btn-sm btn-outline-primary mb-0">
							<i className="uil uil-image-upload me-1"></i>
							{poster.poster_bg ? t('poster_bg_change') : t('poster_bg_upload')}
							<input type="file" accept="image/*" hidden
								onChange={(e) => uploadPosterBg(e.target.files?.[0])} />
						</label>
						{!!poster.poster_bg &&
							<button type="button" className="btn btn-sm btn-outline-danger"
								onClick={() => uploadPosterBg(null, true)}>
								<i className="uil uil-trash-alt me-1"></i>
								{t('delete')}
							</button>
						}
						<span className="text-muted fz-14">{t('poster_bg_hint')}</span>
					</div>

					<div className="d-flex justify-content-center" style={{ 'overflow': 'auto' }}>
						<Poster data={poster} posterRef={posterRef} />
					</div>
					<div className="d-flex gap-2 w-100 mt-3">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => setModalPoster(false)}>
							{t('cancel')}
						</button>
						{/* YUKLAB OLISH — plakat boshqa printerdan chiqariladi (user talabi).
						    Rasm A5 o'lchamida, 3 barobar aniqlikda saqlanadi. */}
						<button type="button" className="btn btn-outline-primary w-100"
							onClick={downloadPoster} disabled={posterSaving}>
							<i className="uil uil-download-alt me-1"></i>
							{posterSaving ? t('loading') : t('download')}
						</button>
						<button type="button" className="btn btn-primary w-100" onClick={handlePrint}>
							<i className="uil uil-print me-1"></i>
							{t('print')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
